import React, { useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
  submitJourneyBotActionPlan,
  submitJourneyBotRawText,
  updateJourneyBotActionItem,
} from './api/journeyBotApi';
import ContextualEarlyAccessNotice from './ContextualEarlyAccessNotice';
import { useData } from './DataProviderContext';

const providerOptions = [
  { value: 'chatgpt', label: 'ChatGPT' },
  { value: 'claude', label: 'Claude' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'other', label: 'Other' },
];

const CREATE_NEW_GOAL = '__create_new_goal__';

const makeJourneyId = (value) => {
  const id = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

  return id || 'journeybot-import';
};

const formatContext = (context) => JSON.stringify(context, null, 2);

export const buildLocalJourneyContext = (dataState) => {
  const journey = dataState?.userDocs || {};
  const steps = Array.isArray(journey.steps) ? journey.steps : [];
  const actions = steps.flatMap((step, stepIndex) => (
    Array.isArray(step?.items) ? step.items.map((item, itemIndex) => {
      const description = typeof item?.value === 'string' ? item.value.trim() : '';
      const title = description.split(/\r?\n/).find(Boolean) || step?.objective || 'Untitled action';
      return {
        id: String(item?.journeyBotActionId || item?.keyID || `action-${stepIndex + 1}-${itemIndex + 1}`),
        title,
        description,
        status: ['active', 'deferred', 'completed'].includes(item?.status) ? item.status : 'active',
        relevance: ['relevant', 'irrelevant', 'unknown'].includes(item?.relevance)
          ? item.relevance
          : 'unknown',
        user_note: typeof item?.user_note === 'string' ? item.user_note : '',
      };
    }) : []
  ));
  const accepted = actions.filter((item) => item.relevance !== 'irrelevant');

  return {
    currentGoal: dataState?.activeGoal?.goal || dataState?.journeyMeta?.title || 'Current journey',
    startState: journey.startState || journey.fromTheme || '',
    shortContext: journey.context || '',
    endState: journey.endState || journey.endTheme || '',
    activeActions: accepted.filter((item) => item.status === 'active'),
    deferredActions: accepted.filter((item) => item.status === 'deferred'),
    completedActions: accepted.filter((item) => item.status === 'completed'),
    rejectedSuggestions: actions.filter((item) => item.relevance === 'irrelevant'),
    blockers: Array.isArray(journey.blockers) ? journey.blockers : [],
    decisions: Array.isArray(journey.decisions) ? journey.decisions : [],
    constraints: Array.isArray(journey.constraints) ? journey.constraints : [],
    nextAction: typeof journey.next_action === 'string'
      ? journey.next_action
      : typeof journey.nextAction === 'string' ? journey.nextAction : '',
  };
};

const buildJourneyBotPrompt = (goal) => {
  const trimmedGoal = String(goal || '').trim();
  const goalLine = trimmedGoal
    ? `Use this goal unless the source clearly says otherwise: ${trimmedGoal}`
    : 'Infer the goal from the source content.';

  return `Convert the content I provide into JSON that can be pasted into JourneyBot.

${goalLine}

Return only valid JSON. Do not wrap it in Markdown. Do not include commentary before or after the JSON.

The top-level JSON object must include exactly these fields:
- goal: string
- startState: string
- context: string
- endState: string
- assumptions: string[]
- constraints: string[]
- decisions: string[]
- action_items: object[]
- blockers: string[]
- open_questions: string[]
- next_action: string
- source_summary: string
- confidence: number from 0 to 1
- created_at: ISO 8601 date-time string with timezone offset

Each action_items entry must include exactly these fields:
- id: unique string using only letters, numbers, underscores, or hyphens
- title: short action title
- description: detailed action description
- status: one of active, deferred, completed
- relevance: one of relevant, irrelevant, unknown
- user_note: empty string unless I explicitly supplied a note

Do not add extra fields. Put priority, effort, due dates, owners, dependencies, or categories inside description if they matter.
Keep context at 300 characters or fewer. If a section has nothing useful, use an empty array or empty string as appropriate.

Content to convert:
[Paste the AI answer, notes, plan, meeting transcript, or research summary here]`;
};

const structuredPlanFields = [
  'goal',
  'context',
  'assumptions',
  'constraints',
  'decisions',
  'action_items',
  'blockers',
  'open_questions',
  'next_action',
  'source_summary',
  'confidence',
  'created_at',
];

const parseJourneyBotActionPlan = (value) => {
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    const candidate = parsed.structured || parsed;
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      return null;
    }

    const hasAllFields = structuredPlanFields.every((field) =>
      Object.prototype.hasOwnProperty.call(candidate, field),
    );

    return hasAllFields
      ? {
        startState: '',
        endState: '',
        ...candidate,
      }
      : null;
  } catch {
    return null;
  }
};

const uniqueGoalOptions = (dataState) => {
  const options = [];
  const seen = new Set();

  const addGoal = (label, description = '') => {
    const trimmed = String(label || '').trim();
    if (!trimmed) return;
    const id = makeJourneyId(trimmed);
    if (seen.has(id)) return;
    seen.add(id);
    options.push({ id, label: trimmed, description });
  };

  (dataState?.userDocs?.steps || []).forEach((step) => {
    addGoal(step.objective, step.objectiveDescription);
  });

  (dataState?.lifeGoals?.goals || []).forEach((goal) => {
    addGoal(goal.goal);
  });

  addGoal(dataState?.loadedGoal?.goal);

  return options;
};

const listSection = (title, values = []) => (
  <Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
      {title}
    </Typography>
    {values.length ? (
      <Stack component="ul" spacing={0.5} sx={{ pl: 3, mt: 0 }}>
        {values.map((value, index) => (
          <Typography component="li" variant="body2" key={`${title}-${index}`}>
            {value}
          </Typography>
        ))}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary">
        None captured.
      </Typography>
    )}
  </Box>
);

function ActionItemCard({ item, onChange, isSaving }) {
  const [note, setNote] = useState(item.user_note || '');

  React.useEffect(() => {
    setNote(item.user_note || '');
  }, [item.user_note]);

  const updateField = (field, value) => {
    onChange(item.id, { [field]: value });
  };

  const saveNote = () => {
    if (note !== (item.user_note || '')) {
      updateField('user_note', note);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Chip size="small" label={item.status} />
              <Chip size="small" label={item.relevance} variant="outlined" />
            </Stack>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {item.title}
            </Typography>
            {item.description && (
              <Typography variant="body2" color="text.secondary">
                {item.description}
              </Typography>
            )}
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={item.status}
                disabled={isSaving}
                onChange={(event) => updateField('status', event.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="deferred">Deferred</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Relevance</InputLabel>
              <Select
                label="Relevance"
                value={item.relevance}
                disabled={isSaving}
                onChange={(event) => updateField('relevance', event.target.value)}
              >
                <MenuItem value="relevant">Relevant</MenuItem>
                <MenuItem value="irrelevant">Irrelevant</MenuItem>
                <MenuItem value="unknown">Unknown</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label="User note"
            value={note}
            disabled={isSaving}
            onChange={(event) => setNote(event.target.value)}
            onBlur={saveNote}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function JourneyImportContent({ embedded = false }) {
  const { dataState, dispatch } = useData();
  const goalOptions = useMemo(() => uniqueGoalOptions(dataState), [dataState]);
  const defaultGoal = goalOptions[0]?.label || '';
  const defaultJourneyId = useMemo(() => makeJourneyId(defaultGoal), [defaultGoal]);

  const [selectedGoalId, setSelectedGoalId] = useState(goalOptions[0]?.id || CREATE_NEW_GOAL);
  const [journeyId, setJourneyId] = useState(defaultJourneyId);
  const [goal, setGoal] = useState(defaultGoal);
  const [newGoalDescription, setNewGoalDescription] = useState('');
  const [sourceProvider, setSourceProvider] = useState('chatgpt');
  const [rawText, setRawText] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [contextText, setContextText] = useState('');
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [isImporting, setIsImporting] = useState(false);
  const [savingActionId, setSavingActionId] = useState('');
  const [loadingContext, setLoadingContext] = useState(false);
  const [isPromptEditorOpen, setIsPromptEditorOpen] = useState(false);
  const [promptDraft, setPromptDraft] = useState(() => buildJourneyBotPrompt(defaultGoal));
  const [isPromptCustomized, setIsPromptCustomized] = useState(false);

  const plan = importResult?.plan;
  const actionItems = plan?.action_items || [];
  const detectedStructuredPlan = useMemo(() => parseJourneyBotActionPlan(rawText), [rawText]);
  const journeyBotPrompt = useMemo(() => buildJourneyBotPrompt(goal), [goal]);

  React.useEffect(() => {
    if (!isPromptCustomized) {
      setPromptDraft(journeyBotPrompt);
    }
  }, [isPromptCustomized, journeyBotPrompt]);

  React.useEffect(() => {
    if (!goalOptions.length) {
      setSelectedGoalId(CREATE_NEW_GOAL);
      return;
    }

    setSelectedGoalId((current) => {
      if (current && current !== CREATE_NEW_GOAL && goalOptions.some((option) => option.id === current)) {
        return current;
      }
      const first = goalOptions[0];
      setGoal(first.label);
      setJourneyId(first.id);
      return first.id;
    });
  }, [goalOptions]);

  const handleGoalTargetChange = (event) => {
    const value = event.target.value;
    setSelectedGoalId(value);
    setImportResult(null);
    setContextText('');

    if (value === CREATE_NEW_GOAL) {
      setGoal('');
      setJourneyId('');
      return;
    }

    const selected = goalOptions.find((option) => option.id === value);
    if (selected) {
      setGoal(selected.label);
      setJourneyId(selected.id);
    }
  };

  const handleImport = async (event) => {
    event.preventDefault();
    setIsImporting(true);
    setStatus({ type: 'loading', message: 'Importing AI response...' });
    setContextText('');

    try {
      const trimmedGoal = goal.trim();
      const structuredPlan = detectedStructuredPlan;
      const importedGoal = trimmedGoal || structuredPlan?.goal || '';
      const targetJourneyId = journeyId || makeJourneyId(importedGoal);

      const result = structuredPlan
        ? await submitJourneyBotActionPlan(targetJourneyId, {
          ...structuredPlan,
          goal: importedGoal,
        })
        : await submitJourneyBotRawText(targetJourneyId, rawText, {
          goal: importedGoal,
          source_provider: sourceProvider,
        });

      dispatch({
        type: 'IMPORT_JOURNEYBOT_PLAN',
        payload: {
          goalName: importedGoal,
          goalDescription: newGoalDescription.trim(),
          plan: result.plan,
        },
      });

      setJourneyId(targetJourneyId);
      setImportResult(result);
      setStatus({
        type: 'success',
        message: structuredPlan
          ? 'Structured JourneyBot action plan imported and added to the board.'
          : 'Raw AI response imported and added to the board.',
      });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Import failed.' });
    } finally {
      setIsImporting(false);
    }
  };

  const handleActionChange = async (actionId, patch) => {
    setSavingActionId(actionId);
    setStatus({ type: 'loading', message: 'Saving action item feedback...' });

    try {
      const result = await updateJourneyBotActionItem(journeyId, actionId, patch);
      setImportResult((current) => {
        if (!current?.plan || !result.actionItem) return current;
        return {
          ...current,
          plan: {
            ...current.plan,
            action_items: current.plan.action_items.map((item) =>
              item.id === actionId ? result.actionItem : item,
            ),
          },
          updatedAt: result.updatedAt,
        };
      });
      setStatus({ type: 'success', message: 'Action item updated.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Could not update action item.' });
    } finally {
      setSavingActionId('');
    }
  };

  const handleFetchContext = () => {
    setLoadingContext(true);
    setStatus({ type: 'loading', message: 'Building compact AI context...' });

    try {
      const context = buildLocalJourneyContext(dataState);
      setContextText(formatContext(context));
      setStatus({ type: 'success', message: 'Current journey context is ready.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Could not build context.' });
    } finally {
      setLoadingContext(false);
    }
  };

  const copyContext = async () => {
    if (!contextText) return;
    await navigator.clipboard.writeText(contextText);
    setStatus({ type: 'success', message: 'Context copied to clipboard.' });
  };

  const copyJourneyBotPrompt = async () => {
    await navigator.clipboard.writeText(promptDraft);
    setStatus({ type: 'success', message: 'JourneyBot AI prompt copied to clipboard.' });
  };

  const dismissError = () => {
    setStatus((current) => (current.type === 'error' ? { type: 'idle', message: '' } : current));
  };

  return (
    <Container
      maxWidth="md"
      disableGutters={embedded}
      sx={{ py: embedded ? 0 : { xs: 2, sm: 4 }, textAlign: 'left' }}
    >
      <Stack spacing={3}>
        {!embedded && (
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
              Import AI Response
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Paste output from ChatGPT, Claude, Gemini, or another AI tool. JourneyBot stores the
              action-memory layer; it does not chat with providers directly.
            </Typography>
          </Box>
        )}

        {status.type !== 'idle' && status.type !== 'error' && (
          <Alert severity={status.type === 'success' ? 'success' : 'info'}>
            {status.message}
          </Alert>
        )}

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
                  Copy this prompt to your AI chat
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ask your AI tool to return JourneyBot JSON, then paste its response below.
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="contained"
                  onClick={copyJourneyBotPrompt}
                  startIcon={<ContentCopyOutlinedIcon />}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                >
                  Copy Prompt
                </Button>
                <Button
                  variant="text"
                  onClick={() => setIsPromptEditorOpen((current) => !current)}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                >
                  {isPromptEditorOpen ? 'Hide prompt' : 'Edit prompt'}
                </Button>
              </Stack>

              {isPromptEditorOpen && (
                <Stack spacing={1}>
                  <TextField
                    label="JourneyBot JSON prompt"
                    value={promptDraft}
                    onChange={(event) => {
                      setPromptDraft(event.target.value);
                      setIsPromptCustomized(true);
                    }}
                    multiline
                    minRows={12}
                    fullWidth
                  />
                  <Button
                    variant="text"
                    onClick={() => {
                      setPromptDraft(journeyBotPrompt);
                      setIsPromptCustomized(false);
                    }}
                    sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                  >
                    Use generated prompt
                  </Button>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card component="form" onSubmit={handleImport} variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Attach AI output to</InputLabel>
                <Select
                  label="Attach AI output to"
                  value={selectedGoalId}
                  onChange={handleGoalTargetChange}
                >
                  {goalOptions.map((option) => (
                    <MenuItem value={option.id} key={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                  <MenuItem value={CREATE_NEW_GOAL}>Create new goal</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label={selectedGoalId === CREATE_NEW_GOAL ? 'New goal title' : 'Goal or title'}
                value={goal}
                onChange={(event) => {
                  setGoal(event.target.value);
                  if (selectedGoalId === CREATE_NEW_GOAL) {
                    setJourneyId(makeJourneyId(event.target.value));
                  }
                }}
                placeholder="Example: Prepare for first home loan application"
                fullWidth
              />

              {selectedGoalId === CREATE_NEW_GOAL && (
                <TextField
                  label="New goal description"
                  value={newGoalDescription}
                  onChange={(event) => setNewGoalDescription(event.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
              )}

              <TextField
                label="JourneyBot record ID"
                value={journeyId || makeJourneyId(goal)}
                onChange={(event) => setJourneyId(makeJourneyId(event.target.value))}
                helperText="Used only for JourneyBot backend storage. Private to your Firebase user."
                required
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Source provider</InputLabel>
                <Select
                  label="Source provider"
                  value={sourceProvider}
                  onChange={(event) => setSourceProvider(event.target.value)}
                >
                  {providerOptions.map((provider) => (
                    <MenuItem value={provider.value} key={provider.value}>
                      {provider.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Raw AI response"
                value={rawText}
                onChange={(event) => setRawText(event.target.value)}
                placeholder="Paste the AI-generated plan here..."
                multiline
                minRows={10}
                required
                fullWidth
              />

              <ContextualEarlyAccessNotice />

              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={isImporting ? <CircularProgress size={18} /> : <UploadFileOutlinedIcon />}
                disabled={!rawText.trim() || !(goal.trim() || detectedStructuredPlan?.goal) || isImporting}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
              >
                Import into Journey
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {plan && (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
                    Imported Action Plan
                  </Typography>
                  <Typography variant="h6" component="p">
                    {plan.goal}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Source summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {plan.source_summary || 'No source summary captured.'}
                  </Typography>
                </Box>

                {listSection('Decisions', plan.decisions)}
                {listSection('Blockers', plan.blockers)}

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Next action
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {plan.next_action || 'No next action captured.'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {plan && (
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mb: 1.5 }}>
              Action Items
            </Typography>
            <Stack spacing={2}>
              {actionItems.length ? (
                actionItems.map((item) => (
                  <ActionItemCard
                    key={item.id}
                    item={item}
                    isSaving={savingActionId === item.id}
                    onChange={handleActionChange}
                  />
                ))
              ) : (
                <Alert severity="info">
                  No action items were extracted. You can still fetch and reuse the compact context
                  based on the imported source text.
                </Alert>
              )}
            </Stack>
          </Box>
        )}

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
                  Export Compact AI Context
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Build a copy-friendly summary from the currently selected journey, including
                  edits that are still only saved in this browser.
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <ContextualEarlyAccessNotice sx={{ flexBasis: '100%' }} />
                <Button
                  variant="outlined"
                  onClick={handleFetchContext}
                  disabled={!dataState?.dataLoaded || loadingContext}
                  startIcon={loadingContext ? <CircularProgress size={18} /> : null}
                >
                  Export Current Context
                </Button>
                <Button
                  variant="text"
                  onClick={copyContext}
                  disabled={!contextText}
                  startIcon={<ContentCopyOutlinedIcon />}
                >
                  Copy
                </Button>
              </Stack>

              <TextField
                label="Compact AI context"
                value={contextText}
                multiline
                minRows={8}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      <Snackbar
        open={status.type === 'error'}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ maxWidth: { xs: 'calc(100% - 32px)', sm: 600 } }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={dismissError}
          sx={{ width: '100%' }}
        >
          {status.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export function JourneyImportPanel() {
  return (
    <Accordion sx={{ mx: { xs: 1, sm: 2 }, my: 2, textAlign: 'left' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Add AI input
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Paste AI output, attach it to an existing goal, or create a new goal.
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <JourneyImportContent embedded />
      </AccordionDetails>
    </Accordion>
  );
}

export default function JourneyImportPage() {
  return <JourneyImportContent />;
}
